/**
 * Keeps the URL's locale segment and i18next's active language as one value.
 *
 * Two directions, both of which have to work or the two halves drift apart:
 *
 *  - Language switcher -> URL. Every switcher in the app (public navbar, admin
 *    header) already calls `i18n.changeLanguage`, so listening for
 *    `languageChanged` rewrites the address bar for all of them at once —
 *    in place, preserving the current route, its params and its query,
 *    because the router's own path never changes.
 *  - URL -> language. Browser back/forward can cross a language boundary
 *    (`/en/about` -> `/about`). The history layer re-reads the locale from the
 *    path on every popstate; this pushes that back into i18next so the copy
 *    follows the URL rather than the other way round.
 *
 * The two listeners cannot loop: each one bails out when the value it would
 * write is the value already in place.
 */
import type { i18n as I18nInstance } from 'i18next'
import { resolveLanguage } from './language'
import type { LocaleAwareHistory } from './locale-history'

export function bindLocaleToHistory(
  i18nInstance: I18nInstance,
  localeHistory: LocaleAwareHistory,
): () => void {
  const handleLanguageChanged = (language: string): void => {
    localeHistory.setLocale(resolveLanguage(language))
  }

  i18nInstance.on('languageChanged', handleLanguageChanged)

  const unsubscribeHistory = localeHistory.history.subscribe(() => {
    const urlLocale = localeHistory.getLocale()
    if (resolveLanguage(i18nInstance.language) !== urlLocale) {
      void i18nInstance.changeLanguage(urlLocale)
    }
  })

  return () => {
    i18nInstance.off('languageChanged', handleLanguageChanged)
    unsubscribeHistory()
  }
}
